import React from 'react';
import { useNavigate } from 'react-router-dom';

const PricingSection = () => {
  const navigate = useNavigate();

  const plans = [
    {
      name: '14-Day Free Trial',
      price: 0,
      priceLabel: 'Free',
      priceSuffix: 'for 14 days',
      description: 'Try the full Marketing Studio — no credit card required',
      popular: false,
      gridColor: '#10b981',
      features: [
        'Full 3-phase Marketing Studio',
        '30-day AI strategy & content calendar',
        'AI captions, hashtags & post briefs',
        'Design Studio (12 templates)',
        'AI image generation',
        'Guided video workflow',
        'Direct Instagram & Facebook publishing',
        'Email support (48h response)',
      ],
      cta: 'Start Free Trial',
      ctaAction: () => navigate('/auth/signup'),
    },
    {
      name: 'Marketing Studio',
      price: 39,
      priceLabel: '£39',
      priceSuffix: '/month',
      description: 'Everything you need to plan, design, and publish your marketing',
      popular: true,
      gridColor: '#2563eb',
      features: [
        'Full 3-phase Marketing Studio',
        '30-day AI strategy & content calendar',
        'AI captions, hashtags & post briefs',
        'Design Studio (12 templates)',
        'AI image generation (6 styles)',
        'Guided video workflow (4-step)',
        'Direct Instagram & Facebook publishing',
        'Campaign progress dashboard & PDF report',
        'Logo background removal',
        'Posting streak & completion tracking',
      ],
      cta: 'Get Started',
      ctaAction: () => navigate('/auth/signup'),
    },
  ];

  return (
    <section id="pricing" className="bg-slate-900 py-16 md:py-20 px-4">
      <div className="max-w-7xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Simple, Transparent Pricing
          </h2>
          <p className="text-sm md:text-base text-slate-400">
            Start free, upgrade as you grow. No hidden fees, cancel anytime.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16 max-w-3xl mx-auto">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`relative bg-slate-800/90 backdrop-blur-sm rounded-lg md:rounded-xl border overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                plan.popular
                  ? 'border-blue-500/60'
                  : 'border-slate-700/60 hover:border-slate-600'
              }`}
            >
              {/* Micro Grid Pattern - Top Left Corner */}
              <div className="absolute top-0 left-0 w-20 md:w-24 h-20 md:h-24 pointer-events-none opacity-30 overflow-hidden z-10">
                <div
                  style={{
                    backgroundImage: `
                      linear-gradient(${plan.gridColor} 0.5px, transparent 0.5px),
                      linear-gradient(90deg, ${plan.gridColor} 0.5px, transparent 0.5px)
                    `,
                    backgroundSize: '8px 8px'
                  }}
                  className="absolute inset-0"
                ></div>
                <div
                  style={{
                    background: 'linear-gradient(135deg, transparent 0%, rgba(15, 23, 42, 0.9) 60%)'
                  }}
                  className="absolute inset-0"
                ></div>
              </div>

              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute top-4 right-4 z-20">
               
                </div>
              )}

              {/* Dark Header */}
              <div className="relative h-12 md:h-14 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
                <div className="relative z-10 h-full flex items-center justify-center px-4">
                  <h3 className="text-base md:text-lg font-bold text-white">{plan.name}</h3>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-5 md:p-6">
                <p className="text-xs md:text-sm text-slate-400 mb-4 md:mb-5 min-h-[40px]">{plan.description}</p>

                {/* Price */}
                <div className="mb-5 md:mb-6">
                  <div className="flex items-baseline mb-1">
                    <span className="text-3xl md:text-4xl font-bold text-white">{plan.priceLabel}</span>
                    <span className="text-sm text-slate-400 ml-1">{plan.priceSuffix}</span>
                  </div>
                </div>

                {/* Features List */}
                <ul className="space-y-2 md:space-y-2.5 mb-6">
                  {plan.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="flex items-start gap-2 text-xs md:text-sm text-slate-300">
                      <span className={`${plan.popular ? 'text-blue-400' : 'text-blue-500'} font-bold flex-shrink-0 mt-0.5`}>✓</span>
                      <span className="leading-tight">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <button
                  onClick={plan.ctaAction}
                  className={`block w-full py-3 rounded-lg text-sm md:text-base font-semibold text-center transition-all duration-200 ${
                    plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-slate-700 text-white hover:bg-slate-600'
                  }`}
                >
                  {plan.cta}
                </button>

                {idx === 0 && (
                  <p className="text-center text-xs text-slate-500 mt-3">
                    Then £39/month — cancel anytime
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Trust Signals */}
        <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8 py-6 md:py-8 px-4 md:px-6 bg-slate-800/50 rounded-lg md:rounded-xl border border-slate-700 backdrop-blur-sm">
          <div className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-slate-300">
            <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
              <span className="text-green-400 text-sm md:text-base">✓</span>
            </div>
            <span>30-day money-back guarantee</span>
          </div>
          <div className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-slate-300">
            <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-400 text-sm md:text-base">🔒</span>
            </div>
            <span>Secure payment with Stripe</span>
          </div>
          <div className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-slate-300">
            <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
              <span className="text-purple-400 text-sm md:text-base">↻</span>
            </div>
            <span>Cancel anytime</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
